import { stateGo } from 'redux-ui-router';
import { memberStorage } from 'utils';
import { selectVideos } from 'logic/videoResume';
import { selectReviewsStats, fetchReviewsStats } from 'logic/reviews';

import {
  selectMember,
  selectMemberLevel,
  selectMemberIsVegetarian,
  selectSubscription,
  selectSubscriptionHasStarted,
} from 'logic/member';

import {
  selectDefaultMealPlanId,
  selectProduct,
  selectWeek,
  selectWeekWorkouts,
} from 'logic/product';

import {
  fetchMealPlan,
  fetchMealPlanForWeek,
  selectMealPlanIdForWeek,
  fetchMealPlanSwaps,
  selectMealPlanList,
} from 'logic/mealPlan';

import {
  selectCompletions,
  selectRecentlyCompleted,
  mealPlanComplete,
  mealPlanUncomplete,
  workoutComplete,
  workoutUncomplete,
  workoutToggleComplete,
  mealPlanToggleComplete,
  clearComplete,
} from 'logic/completions';

export const ProgramWeek = {
  template: `
    <div class="program-week"
      ng-init="$ctrl.showComments = false"
      ng-if="$ctrl.member">
      <program-nav></program-nav>
      <div class="overview-row"
        ng-class="{
          'mb-0': $ctrl.week.weeklyVideos.length && !$ctrl.showComments && $ctrl.hasStarted
        }">
        <program-week-overview
          is-loading="$ctrl.reviewsIsLoading"
          heading="$ctrl.week.title"
          description="$ctrl.week.prepTips"
          video-id="$ctrl.week.introVideo.brightcoveId"
          comments-count="$ctrl.reviewsStats.reviewsCount"
          comments-new-count="$ctrl.reviewsStats.newReviewsCount"
          replies-count="$ctrl.reviewsStats.repliesCount"
          toggle-comments="$ctrl.onToggleComments()">
        </program-week-overview>
      </div>
      <div class="program-section-row bg-gray-100 border border-y"
        id="comments"
        ng-show="$ctrl.showComments">
        <div class="py-3 py-md-5 container" ng-if="$ctrl.week.introVideo">
          <reviews
            wording-singular="Comment"
            wording-plural="Comments"
            reviewable-type="BrightcoveVideo"
            reviewable-id="$ctrl.week.introVideo.id"
            form-top="true"
            on-cancel="$ctrl.showComments = false">
          </reviews>
        </div>
      </div>
      <program-not-started has-started="$ctrl.hasStarted">
        <div class="mb-4" ng-if="$ctrl.week.weeklyVideos.length">
          <program-week-videos
            week-number="$ctrl.week.weekNumber"
            items="$ctrl.week.weeklyVideos"
            video-resume=$ctrl.videoResume>
          </program-week-videos>
        </div>
        <div class="d-flex"
          ng-class="{
            'flex-column': !$ctrl.isNutrition,
            'flex-column-reverse': $ctrl.isNutrition
          }">
          <div class="bg-gray-100 border border-y"
            ng-class="{ 'program-section-row': !$ctrl.isNutrition }">
            <program-week-workouts
              completions="$ctrl.completions.items.workouts"
              member="$ctrl.member"
              voome-product-id="$ctrl.voomeProductId"
              week-number="$ctrl.week.weekNumber"
              items="$ctrl.workouts"
              on-card-click="$ctrl.stateGo('program.week.workout', { id, exercisePlanId })"
              on-completer-toggle="$ctrl.workoutToggleComplete(exercisePlanId, dayId, voomeProductId, weekNumber, title, isComplete)">
            </program-week-workouts>
          </div>
          <div class="bg-gray-100 border border-y"
            ng-class="{ 'program-section-row': $ctrl.isNutrition }">
            <program-week-meal-plan
              completions="$ctrl.completions.items.meals"
              program="$ctrl.program"
              week-number="$ctrl.week.weekNumber"
              is-loading="$ctrl.mealPlanIsLoading"
              meal-plan-id="$ctrl.mealPlanId"
              meal-plan-list="$ctrl.mealPlanList"
              on-card-click="$ctrl.stateGo('program.week.recipe', { recipeId })"
              on-completer-toggle="$ctrl.mealPlanToggleComplete(mealPlanId, dayString, voomeProductId, weekNumber, title, isComplete)">
            </program-week-meal-plan>
          </div>
        </div>
      </program-not-started>
      <completer-notification
        week-number="$ctrl.week.weekNumber"
        count="$ctrl.completions.total"
        total="$ctrl.workouts.length + $ctrl.week.mealPlan.daysCount"
        heading="$ctrl.recentlyCompleted.title">
      </completer-notification>
    </div>`,

  controller($ngRedux, $location, $anchorScroll) {
    'ngInject';

    return {
      $onInit() {
        const connect = $ngRedux.connect(this.mapState, this.mapDispatch());
        this.disconnect = connect(this);

        // Fetch the meal plan for the week.
        if (!this.mealPlanList.length) {
          const { product, router } = $ngRedux.getState();
          const { weekNumber } = router.currentParams;

          this.fetchMealPlanForWeek({
            level: this.level,
            mealPlanId: this.mealPlanId,
            memberId: this.member.id,
            subscriptionId: this.subscriptionId,
            weekId: this.week.id,
          });
        }

        // Fetch review stats for the intro video.
        this.fetchReviewsStats(
          this.member.id,
          this.voomeProductId,
          this.week.introVideo && this.week.introVideo.id,
          'BrightcoveVideo'
        );
      },

      $onDestroy() {
        this.disconnect();
        this.clearComplete();
      },

      onToggleComments() {
        this.showComments = !this.showComments;

        if (this.showComments) {
          $location.hash('comments');
          $anchorScroll();
        } else {
          $location.hash(null);
        }
      },

      mapState(state) {
        const {
          reviews,
          router,
          mealPlan,
          videoResume,
          member,
          product,
        } = state;

        const { voomeProductId, weekNumber } = router.currentParams;
        const program = selectProduct(product);
        const week = selectWeek(product, weekNumber);
        const isNutrition = program.productMeta.programType === 'Nutrition';

        const mealPlanId =
          selectMealPlanIdForWeek(mealPlan, week && week.id) ||
          selectDefaultMealPlanId(
            product,
            weekNumber,
            selectMemberIsVegetarian(member)
          );

        return {
          isNutrition,
          mealPlanId,
          program,
          voomeProductId,

          // Member
          hasStarted: selectSubscriptionHasStarted(member, voomeProductId),
          level: selectMemberLevel(member),
          member: selectMember(member),
          subscriptionId: selectSubscription(member).id,

          // Program
          workouts: selectWeekWorkouts(product, weekNumber),
          week: selectWeek(product, weekNumber),

          // Completions
          completions: selectCompletions(state, weekNumber),
          recentlyCompleted: selectRecentlyCompleted(state),

          // Meal Plan
          mealPlanIsLoading: mealPlan.isLoading,
          mealPlanList: selectMealPlanList(mealPlan, mealPlanId),

          // Reviews
          reviewsStats: selectReviewsStats(reviews),
          reviewsIsLoading: reviews.isLoading,

          // Videos
          videoResume: selectVideos(videoResume),
        };
      },

      mapDispatch() {
        return {
          clearComplete,
          fetchMealPlan,
          fetchMealPlanSwaps,
          fetchMealPlanForWeek,
          fetchReviewsStats,
          mealPlanToggleComplete,
          workoutToggleComplete,
          stateGo,
        };
      },
    };
  },
};



// WEBPACK FOOTER //
// ./src/containers/Program/Week.js